import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Alert,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useWhatsAppConfig, useWhatsAppConfigStatus } from '../hooks/useWhatsAppConfig';
import { EmbeddedSignupButton } from '../components/EmbeddedSignupButton';

export const WhatsAppSettings: React.FC = () => {
  const { data: config, isLoading, error, refetch } = useWhatsAppConfig();
  const { data: status, refetch: refetchStatus } = useWhatsAppConfigStatus(config?.id);
  const provisioningStatus = status?.status || config?.provisioning_status;
  const isHealthy = provisioningStatus === 'active';
  const statusIssues = [
    status?.provisioning_error,
    !status?.api_connection && provisioningStatus !== 'active'
      ? 'La connexion Meta reste à finaliser.'
      : null,
  ].filter(Boolean);

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography>Chargement de la configuration...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Impossible de charger la configuration WhatsApp : {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  const needsInitialSetup = !config?.business_phone_number;

  if (needsInitialSetup) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body1">
              Votre configuration WhatsApp doit encore être complétée.
            </Typography>
            <Button
              variant="contained"
              href="/whatsapp/setup"
              sx={{ mt: 2 }}
            >
                Compléter la configuration
            </Button>
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon />
            Paramètres WhatsApp Business
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gérez votre configuration et surveillez le statut de votre agent FAQ
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Informations générales */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon />
                  Configuration
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Numéro WhatsApp Business
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {config.business_phone_number}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nom de l'agent
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {config.agent_name}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Statut
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                      label={config.is_active ? 'Actif' : 'Inactif'}
                      color={config.is_active ? 'success' : 'default'}
                      size="small"
                      icon={config.is_active ? <CheckIcon /> : <ErrorIcon />}
                    />
                    <Chip
                      label={config.is_verified ? 'Vérifié' : 'Non vérifié'}
                      color={config.is_verified ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Statut du système */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  État du système
                </Typography>
                
                {status ? (
                  <Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Santé du système
                      </Typography>
                      <Chip
                        label={isHealthy ? 'Opérationnel' : provisioningStatus || 'En attente'}
                        color={isHealthy ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                    
                    {status.last_webhook_received && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Dernier webhook reçu
                        </Typography>
                        <Typography variant="body2">
                          {new Date(status.last_webhook_received).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    
                    {statusIssues.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Problèmes détectés
                        </Typography>
                        {statusIssues.map((issue, index) => (
                          <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                            <Typography variant="body2">{issue}</Typography>
                          </Alert>
                        ))}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Chargement du statut...
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Actions disponibles
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="outlined" href="/whatsapp/setup?edit=1">
                  Modifier la configuration
                </Button>
              </Box>
              {!isHealthy && config?.id && (
                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                  <EmbeddedSignupButton
                    configId={config.id}
                    onConnected={async () => {
                      await Promise.all([refetch(), refetchStatus()]);
                    }}
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};
